import type { MembershipRole } from '@services/auth.service';
import type { NavItemConfig, NavSectionConfig } from './navigation';

export interface RoleWorkspaceConfig {
  role: MembershipRole;
  label: string;
  description: string;
  landingPath: string;
  businessTitles: string[];
}

const ADMIN_NAV: NavSectionConfig[] = [
  {
    name: 'Organization',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'Branches', path: '/settings', classification: 'production' },
      { name: 'Users', path: '/settings', classification: 'production' },
      { name: 'Customer master', path: '/settings/customers', classification: 'production' },
      { name: 'Vendor master', path: '/settings/vendors', classification: 'production' },
      { name: 'Payment modes', path: '/settings/payment-modes', classification: 'production' },
      { name: 'Contract Master', path: '/settings/rate-master', classification: 'production' },
      { name: 'Audit log', path: '/settings/audit', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
      { name: 'Fleet', path: '/fleet', classification: 'production' },
      { name: 'Settings', path: '/settings', classification: 'production' },
    ],
  },
  {
    name: 'Planning',
    items: [
      { name: 'MDR queue', path: '/planning/requisitions', classification: 'production' },
      { name: 'Vendor quotes', path: '/planning/auctions', classification: 'production' },
      { name: 'Approval inbox', path: '/planning/approvals', classification: 'production' },
      { name: 'Route Planning', path: '/route-planning', classification: 'production' },
    ],
  },
  {
    name: 'Operations',
    items: [
      { name: 'Dockets', path: '/dockets', classification: 'production' },
      { name: 'Dispatch', path: '/dispatch', classification: 'production' },
      { name: 'Tracking', path: '/tracking', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
      { name: 'Carriers', path: '/carriers', classification: 'production' },
      { name: 'Billing', path: '/billing', classification: 'production' },
      { name: 'Analytics', path: '/analytics', classification: 'production' },
      { name: 'Reports', path: '/reports', classification: 'production' },
      { name: 'Commercial Analytics', path: '/reports/commercial', classification: 'production' },
    ],
  },
];

const OPS_NAV: NavSectionConfig[] = [
  {
    name: 'Operations',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'MDR queue', path: '/planning/requisitions', classification: 'production' },
      { name: 'Vendor quotes', path: '/planning/auctions', classification: 'production' },
      { name: 'Dockets', path: '/dockets', classification: 'production' },
      { name: 'Dispatch', path: '/dispatch', classification: 'production' },
      { name: 'Fleet', path: '/fleet', classification: 'production' },
      { name: 'Route Planning', path: '/route-planning', classification: 'production' },
      { name: 'Tracking', path: '/tracking', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
    ],
  },
];

const DISPATCHER_NAV: NavSectionConfig[] = [
  {
    name: 'Dispatch',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'Dispatch board', path: '/dispatch', classification: 'production' },
      { name: 'Drivers', path: '/fleet?tab=drivers', classification: 'production' },
      { name: 'Route Planning', path: '/route-planning', classification: 'production' },
      { name: 'Tracking', path: '/tracking', classification: 'production' },
      { name: 'Dockets', path: '/dockets', classification: 'production' },
    ],
  },
  {
    name: 'Planning',
    items: [
      { name: 'MDR queue', path: '/planning/requisitions', classification: 'production' },
      { name: 'Vendor quotes', path: '/planning/auctions', classification: 'production' },
      { name: 'Pending approval', path: '/planning/approvals', classification: 'production' },
    ],
  },
];

const APPROVER_NAV: NavSectionConfig[] = [
  {
    name: 'Approvals',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'Approval inbox', path: '/planning/approvals', classification: 'production' },
      { name: 'MDR queue', path: '/planning/requisitions', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
      { name: 'Dockets', path: '/dockets', classification: 'production' },
    ],
  },
];

const ACCOUNTING_NAV: NavSectionConfig[] = [
  {
    name: 'Finance',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'Dockets', path: '/dockets', classification: 'production' },
      { name: 'Billing & invoices', path: '/billing', classification: 'production' },
      { name: 'Analytics', path: '/analytics', classification: 'production' },
      { name: 'Reports', path: '/reports', classification: 'production' },
      { name: 'Commercial Analytics', path: '/reports/commercial', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
    ],
  },
];

const DRIVER_NAV: NavSectionConfig[] = [
  {
    name: 'My work',
    items: [
      { name: 'My trips', path: '/my-trips', classification: 'production' },
      { name: 'Notifications', path: '/notifications', classification: 'production' },
      { name: 'Tracking', path: '/tracking', classification: 'production' },
    ],
  },
];

const VIEWER_NAV: NavSectionConfig[] = [
  {
    name: 'Read only',
    items: [
      { name: 'Dashboard', path: '/dashboard', classification: 'production' },
      { name: 'Dockets', path: '/dockets', classification: 'production' },
      { name: 'Tracking', path: '/tracking', classification: 'production' },
      { name: 'Analytics', path: '/analytics', classification: 'production' },
      { name: 'Commercial Analytics', path: '/reports/commercial', classification: 'production' },
    ],
  },
];

export const ROLE_WORKSPACES: Record<MembershipRole, RoleWorkspaceConfig & { nav: NavSectionConfig[] }> = {
  OWNER: {
    role: 'OWNER',
    label: 'Tenant Admin',
    description: 'Full organization administration',
    landingPath: '/dashboard',
    businessTitles: ['Tenant Admin'],
    nav: ADMIN_NAV,
  },
  ADMIN: {
    role: 'ADMIN',
    label: 'Tenant Admin',
    description: 'Organization administration',
    landingPath: '/dashboard',
    businessTitles: ['Tenant Admin'],
    nav: ADMIN_NAV,
  },
  OPERATIONS_MANAGER: {
    role: 'OPERATIONS_MANAGER',
    label: 'Operations Manager',
    description: 'Requisitions, dispatch, and fleet operations',
    landingPath: '/dashboard',
    businessTitles: ['Operations Manager', 'Traffic Executive'],
    nav: OPS_NAV,
  },
  DISPATCHER: {
    role: 'DISPATCHER',
    label: 'Dispatcher',
    description: 'Daily dispatch and assignments',
    landingPath: '/dashboard',
    businessTitles: ['Dispatcher'],
    nav: DISPATCHER_NAV,
  },
  APPROVER: {
    role: 'APPROVER',
    label: 'Approver',
    description: 'Reviews and decides MDR approvals',
    landingPath: '/planning/approvals',
    businessTitles: ['Approver'],
    nav: APPROVER_NAV,
  },
  ACCOUNTING: {
    role: 'ACCOUNTING',
    label: 'Account Manager',
    description: 'Billing and revenue',
    landingPath: '/dashboard',
    businessTitles: ['Account Manager'],
    nav: ACCOUNTING_NAV,
  },
  DRIVER: {
    role: 'DRIVER',
    label: 'Driver',
    description: 'Assigned trips and documents',
    landingPath: '/my-trips',
    businessTitles: ['Driver'],
    nav: DRIVER_NAV,
  },
  VIEWER: {
    role: 'VIEWER',
    label: 'Viewer',
    description: 'Read-only access',
    landingPath: '/dashboard',
    businessTitles: ['Viewer'],
    nav: VIEWER_NAV,
  },
};

export function getRoleWorkspace(role: MembershipRole | null | undefined) {
  if (!role) return ROLE_WORKSPACES.VIEWER;
  return ROLE_WORKSPACES[role] ?? ROLE_WORKSPACES.VIEWER;
}

export function getRoleLandingPath(role: MembershipRole | null | undefined): string {
  return getRoleWorkspace(role).landingPath;
}

export function getRoleNavigation(role: MembershipRole | null | undefined): NavSectionConfig[] {
  return getRoleWorkspace(role).nav;
}
